import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'

import { memo, useMemo, useCallback } from 'react'

import { useResponsive } from 'hooks/useResponsive'
import { useDropdownState } from 'hooks/useDropdownState'
import PlaybackSpeedToggler from './Components/Toggler'
import PlaybackSpeedMenuContent from './Web'
import MobilePlaybackSpeedMenu from './Mobile'
import * as Styled from './styled'
import { useMobileInnerSliderCallbacks } from 'hooks/useMobileInnerSliderCallbacks'

interface IProps {
  initialValue: number | string
  onClick: (value: number) => void
  scrollableParentRef?: React.RefObject<HTMLDivElement | null>
}

const MediaPlaybackSpeedDropdown: React.FC<IProps> = ({
  initialValue,
  onClick,
  scrollableParentRef
}) => {
  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const { openCallback, closeCallback } = useMobileInnerSliderCallbacks()

  const { isOpen, toggleDropdown, closeDropdown } = useDropdownState(
    openCallback,
    closeCallback
  )

  const onValueClick = useCallback(
    (value: number) => {
      if (value !== initialValue) {
        onClick(value)
      }
      closeDropdown()
    },
    [initialValue, onClick, closeDropdown]
  )

  const numericPlaybackSpeed = (() => {
    const n =
      typeof initialValue === 'number' ? initialValue : Number(initialValue)
    return Number.isFinite(n) ? n : 1
  })()

  const headerContent = useMemo(
    () => (
      <PlaybackSpeedToggler
        key={'playback-speed-toggler'}
        onToggleMenu={toggleDropdown}
        playbackSpeed={numericPlaybackSpeed}
        scrollableParentRef={scrollableParentRef}
      />
    ),
    [toggleDropdown, numericPlaybackSpeed, scrollableParentRef]
  )

  const mainContent = useMemo(() => {
    return (
      <PlaybackSpeedMenuContent
        selectedValue={initialValue}
        handleValueClick={onValueClick}
      />
    )
  }, [initialValue, onValueClick])

  if (isTablet && isOpen) {
    return (
      <>
        {headerContent}
        <MobilePlaybackSpeedMenu
          key={'mobile-playback-speed-menu'}
          onBack={closeDropdown}
          selectedValue={initialValue}
          handleValueClick={onValueClick}
          isOpen={isOpen}
          onClose={closeDropdown}
        />
      </>
    )
  }

  return (
    <Styled.PlaybackSpeedDropdownRoot
      headerContent={headerContent}
      mainContent={mainContent}
      isOpen={isOpen}
      toggleDropdown={toggleDropdown}
      closeDropdown={closeDropdown}
      scrollableParentRef={scrollableParentRef}
      hasTogglerArrow={false}
    />
  )
}

export default memo(MediaPlaybackSpeedDropdown)
